import { Card, CardContent, cardVariants, type CardProps } from '@/components/ui/card'
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  type TabsContentProps,
  type TabsListProps,
  type TabsProps,
  type TabsTriggerProps,
} from '@/components/ui/tabs'
import { Typography } from '@/components/ui/typography'
import { cn } from '@/lib/utils'
import type { ComponentProps, ComponentType, ReactNode } from 'react'

type DetailIcon = ComponentType<{ className?: string }>

const detailWellToneClasses = {
  neutral: 'border-border/55 bg-elevated dark:border-border/45 dark:bg-elevated',
  amethyst:
    'border-selection-border/25 bg-accentSecondary/45 dark:border-border/45 dark:bg-elevated',
  periwinkle: 'border-brand-periwinkle/25 bg-accent/45 dark:border-border/45 dark:bg-elevated',
  mint: 'border-chart-3/25 bg-brand-mint/70 dark:border-border/45 dark:bg-elevated',
  success:
    'border-status-success-border/50 bg-status-success-background dark:border-border/45 dark:bg-elevated',
  warning:
    'border-status-warning-border/50 bg-status-warning-background dark:border-border/45 dark:bg-elevated',
  destructive:
    'border-status-destructive-border/50 bg-status-destructive-background dark:border-border/45 dark:bg-elevated',
  info: 'border-status-info-border/50 bg-status-info-background dark:border-border/45 dark:bg-elevated',
}

export type DetailWellTone = keyof typeof detailWellToneClasses

export type DetailWellClassOptions = {
  compact?: boolean
  tone?: DetailWellTone
  className?: string
}

export function getDetailWellClass({
  compact = false,
  tone = 'neutral',
  className,
}: DetailWellClassOptions = {}) {
  return cn(
    'rounded-md border',
    compact ? 'px-3 py-2.5' : 'px-3 py-3',
    detailWellToneClasses[tone] ?? detailWellToneClasses.neutral,
    className
  )
}

export const detailSoftWellClass = getDetailWellClass()

export const detailCompactWellClass = getDetailWellClass({ compact: true })

export const detailPaleIconClass = 'size-4 shrink-0 stroke-muted-foreground'

export const detailSurfaceActionClass =
  'size-9 rounded-full border-transparent bg-transparent hover:bg-accentSecondary'

export type DetailCardAccent = NonNullable<CardProps['accent']>

type DetailCardSurface = NonNullable<CardProps['surface']>

const detailCardSurfaceByAccent: Partial<Record<DetailCardAccent, DetailCardSurface>> = {
  info: 'info',
  warning: 'warning',
  destructive: 'destructive',
  success: 'success',
}

export const detailIconBadgeToneClasses = {
  neutral: 'stroke-muted-foreground',
  brand: 'stroke-interaction',
  amethyst: 'stroke-chart-1',
  periwinkle: 'stroke-chart-2',
  mint: 'stroke-chart-3',
  info: 'stroke-status-info-foreground',
  warning: 'stroke-status-warning-foreground',
  destructive: 'stroke-status-destructive-foreground',
  success: 'stroke-status-success-foreground',
}

export type DetailIconTone = keyof typeof detailIconBadgeToneClasses

export const detailSectionSurfaceBaseClass =
  'relative min-w-0 space-y-3 overflow-hidden bg-card px-5 py-4'

export const detailSectionSurfaceToneClasses = {
  amethyst: 'bg-card',
  periwinkle: 'bg-card',
  mint: 'bg-card',
}

export type DetailSectionSurfaceTone = keyof typeof detailSectionSurfaceToneClasses

export const detailRecipientSurfaceClass = detailSectionSurfaceToneClasses.amethyst

export const detailWorkspaceTabPanelClass =
  'min-w-0 overflow-hidden rounded-xl rounded-tl-none border border-t-0 border-border bg-card py-0 shadow-none'

export const detailWorkspaceOverviewClass =
  '@container/summary border-b border-border/40 bg-elevated/30 px-4 py-4 sm:px-5 sm:py-5 dark:bg-card'

export const detailDataHeaderClass = 'border-b border-border/40 bg-elevated/30 dark:bg-elevated/20'

export const detailDataListClass = 'divide-y divide-border/50'

export const detailDataRowClass = 'transition-colors hover:bg-elevated/30'

export const detailInsetListClass =
  'overflow-hidden rounded-md border border-border/55 bg-elevated dark:border-border/45 dark:bg-elevated'

export type DetailCardClassOptions = {
  accent?: DetailCardAccent
  warning?: boolean
  className?: string
}

export function getDetailCardClass({
  accent = 'neutral',
  warning = false,
  className,
}: DetailCardClassOptions = {}) {
  return cardVariants({
    accent,
    surface: warning ? 'warning' : (detailCardSurfaceByAccent[accent] ?? 'default'),
    className: cn('gap-0 overflow-hidden py-0', className),
  })
}

export type DetailCardProps = Omit<CardProps, 'accent'> & {
  accent?: DetailCardAccent
  warning?: boolean
  contentClassName?: string
}

export function DetailCard({
  accent = 'neutral',
  warning = false,
  className,
  contentClassName = 'p-5',
  children,
  ...props
}: DetailCardProps) {
  const surface = warning ? 'warning' : (detailCardSurfaceByAccent[accent] ?? 'default')

  return (
    <Card
      accent={accent}
      surface={surface}
      className={cn('gap-0 overflow-hidden py-0', className)}
      {...props}
    >
      <CardContent className={contentClassName}>{children}</CardContent>
    </Card>
  )
}

export type DetailWellProps = ComponentProps<'div'> & {
  compact?: boolean
  tone?: DetailWellTone
}

export function DetailWell({
  compact = false,
  tone = 'neutral',
  className,
  children,
  ...props
}: DetailWellProps) {
  return (
    <div className={getDetailWellClass({ compact, tone, className })} {...props}>
      {children}
    </div>
  )
}

export type DetailIconBadgeProps = {
  icon: DetailIcon
  tone?: DetailIconTone
  className?: string
  iconClassName?: string
}

export function DetailIconBadge({
  icon: Icon,
  tone = 'neutral',
  className,
  iconClassName,
}: DetailIconBadgeProps) {
  return (
    <div className={cn('flex size-6 shrink-0 items-start justify-center pt-0.5', className)}>
      <Icon
        className={cn(
          'size-5 shrink-0',
          detailIconBadgeToneClasses[tone] ?? detailIconBadgeToneClasses.neutral,
          iconClassName
        )}
      />
    </div>
  )
}

export type DetailSectionSurfaceProps = ComponentProps<'section'> & {
  tone?: DetailSectionSurfaceTone
}

export function DetailSectionSurface({
  tone = 'amethyst',
  className,
  ...props
}: DetailSectionSurfaceProps) {
  return (
    <section
      className={cn(
        detailSectionSurfaceBaseClass,
        detailSectionSurfaceToneClasses[tone] ?? detailSectionSurfaceToneClasses.amethyst,
        className
      )}
      {...props}
    />
  )
}

export type DetailWorkspaceTabsProps = TabsProps

export function DetailWorkspaceTabs({ className, ...props }: DetailWorkspaceTabsProps) {
  return <Tabs size="comfortable" className={cn('w-full min-w-0 gap-0', className)} {...props} />
}

export type DetailWorkspaceTabsListProps = TabsListProps

export function DetailWorkspaceTabsList({ className, ...props }: DetailWorkspaceTabsListProps) {
  return (
    <TabsList
      className={cn('min-w-0 gap-1 pretty-scrollbar', className)}
      layout="scroll"
      {...props}
    />
  )
}

export type DetailWorkspaceTabProps = TabsTriggerProps & {
  icon?: DetailIcon
}

export function DetailWorkspaceTab({
  icon: Icon,
  className,
  children,
  ...props
}: DetailWorkspaceTabProps) {
  return (
    <TabsTrigger className={className} {...props}>
      {Icon ? <Icon className="size-4 sm:size-5" /> : null}
      {children}
    </TabsTrigger>
  )
}

export type DetailWorkspacePanelProps = TabsContentProps

export function DetailWorkspacePanel({ className, ...props }: DetailWorkspacePanelProps) {
  return <TabsContent className={cn(detailWorkspaceTabPanelClass, className)} {...props} />
}

export type DetailWorkspaceOverviewProps = {
  icon: DetailIcon
  title?: ReactNode
  description?: ReactNode
  children?: ReactNode
  className?: string
}

export function DetailWorkspaceOverview({
  icon: Icon,
  title,
  description,
  children,
  className,
}: DetailWorkspaceOverviewProps) {
  return (
    <div className={cn(detailWorkspaceOverviewClass, className)}>
      <div
        className={cn(
          'grid gap-4',
          children &&
            '@[70rem]/summary:grid-cols-[minmax(0,1.15fr)_minmax(26rem,0.95fr)] @[70rem]/summary:items-start'
        )}
      >
        <div className="min-w-0">
          <div className="flex items-start gap-3">
            <DetailIconBadge icon={Icon} tone="brand" />
            <div className="min-w-0">
              <Typography variant="eyebrow">Overview</Typography>
              <Typography variant="cardTitle" className="mt-1 pb-0">
                {title}
              </Typography>
            </div>
          </div>
          <Typography
            variant="body"
            tone="muted"
            className="mt-3 block max-w-3xl sm:text-[0.9375rem]/6"
          >
            {description}
          </Typography>
        </div>

        {children ? <div className="min-w-0">{children}</div> : null}
      </div>
    </div>
  )
}

export type DetailMetricWellProps = Omit<DetailWellProps, 'compact'> & {
  label?: ReactNode
  value?: ReactNode
  detail?: ReactNode
  icon?: ComponentType<{ className?: string; 'aria-hidden'?: boolean | 'true' | 'false' }>
  valueClassName?: string
}

export function DetailMetricWell({
  label,
  value,
  detail,
  icon: Icon,
  tone = 'neutral',
  valueClassName,
  className,
  ...props
}: DetailMetricWellProps) {
  return (
    <DetailWell compact tone={tone} className={cn('min-w-0 space-y-2', className)} {...props}>
      <Typography variant="eyebrow">{label}</Typography>
      <div className="flex min-w-0 items-center gap-2">
        {Icon ? <Icon className="size-4 shrink-0" aria-hidden="true" /> : null}
        <p className={cn('min-w-0 text-lg font-semibold tabular-nums', valueClassName)}>{value}</p>
      </div>
      {detail ? (
        <Typography variant="supporting" className="block">
          {detail}
        </Typography>
      ) : null}
    </DetailWell>
  )
}
